import { HttpClientRequestConfig } from '@deepcloud/api-http-client'
import { calculateFileSizePercentage } from '@deepcloud/deep-ui-utils'
import { useApi } from '@/composables/api.ts'
import type { TaskUploadSection } from '@/components/upload/types'
import type { Node } from '@deepcloud/api-deep-box'
import type { AxiosProgressEvent, AxiosRequestConfig } from 'axios'

type UploadSingleFileApiConfig = HttpClientRequestConfig &
  Pick<AxiosRequestConfig, 'onUploadProgress'> & {
    id: number
    section: TaskUploadSection
    waitForAnalyze?: number
  }

export function useNodeUploader() {
  const { deepBoxAPI } = useApi()

  /**
   * Upload a single file. Returns uploaded node from API response.
   * onProgress: receives percent (0..100)
   */
  async function uploadFile({
    section,
    parentNodeId,
    file,
    typeId,
    boxId,
    onProgress,
    configOverrides,
    abortController,
  }: {
    section: TaskUploadSection
    parentNodeId: string | null
    file: File
    typeId: string
    boxId: string
    onProgress?: (
      percent: number,
      uploadedBytes?: number,
      totalBytes?: number,
    ) => void
    configOverrides?: Partial<UploadSingleFileApiConfig>
    abortController?: AbortController
  }): Promise<Node> {
    /*
     * Early abort the call if file is > 2GB ( API LIMIT )
     * The error should simulate the API error
     * If the API supports the early about, then this case be removed
     * 2GB = 2147483648
     */
    const MAX_FILE_SIZE = 2147483648 // 2GB

    if (file.size > MAX_FILE_SIZE) {
      return Promise.reject({
        response: {
          status: 413,
          data: {
            type: 'about:blank',
            title: 'Payload Too Large',
            status: 413,
            detail: 'Maximum upload size exceeded',
            instance: `/api/v1/deepBoxes/${typeId}/boxes/${boxId}/files`,
          },
        },
      })
    }

    const config: UploadSingleFileApiConfig = {
      headers: { 'content-type': 'multipart/form-data' },
      id: Math.floor(Math.random() * 1000000),
      section,
      signal: abortController?.signal,
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        const total = progressEvent.total || 0
        const loaded = progressEvent.loaded || 0
        const percent = calculateFileSizePercentage(loaded, total)
        onProgress?.(percent ?? 0, loaded, total)
      },
      ...configOverrides,
    }

    let res

    if (section === 'inbox') {
      res = await deepBoxAPI.value.deepBoxesBoxesQueue.create(
        typeId,
        boxId,
        { files: [file] },
        config,
      )
      return res.data[0]
    } else if (parentNodeId) {
      // if we're in a sub-folder, use parentNodeId
      res = await deepBoxAPI.value.deepBoxesBoxesFiles.createById(
        typeId,
        boxId,
        parentNodeId,
        { files: [file] },
        config,
      )
      return res.data[0]
    } else {
      // if we're in files root
      res = await deepBoxAPI.value.deepBoxesBoxesFiles.create(
        typeId,
        boxId,
        { files: [file] },
        config,
      )
      return res.data[0]
    }
  }

  return { uploadFile }
}
